import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import LargeUserCircle from '@mds/icons/UserCircle';
import { Checkbox, TextField } from 'components/ui/FormControls';
import { Box, Typography } from '@mui/material';
import React from 'react';
import ManageTemplate, {
    ManageTemplateInstance,
} from '../../ui/ManageTemplate';

import { DELETE_USER, EDIT_USER, USER } from '../../../gql/users';
import FormTextField from '../../ui/form/FormTextField';
import { User } from '../../../shared/types';
import moment from 'moment';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { useUser } from '../../providers/UserProvider';
import { ResearchRole } from '../../../shared/global-enums';
import { sanitizeRouteId } from '../../../utils/route-params.utils';

import { useParams } from 'react-router-dom';
import {
    useFormControl,
    useFormGroup,
    useManageTemplateForm,
} from '../../../utils/form.utils';
import { useQuery } from '@apollo/react-hooks';
import UserChannelSubscriptions from './UserChannelSubscriptions/UserChannelSubscriptions';
const returnListUrl = baseUrls.publisher.users.list;

const UserEdit = () => {
    const params = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(params._id);
    const { hasScope } = useUser();
    const isAdmin = [ResearchRole.Administrator].some(hasScope);

    const form = useFormGroup({
        position: useFormControl<string>(''),
        displayAvatar: useFormControl<boolean>(false),
        tableauAccess: useFormControl<boolean>(false),
    });

    const { loading, data } = useQuery<{ document: User }>(USER, {
        skip: !_id,
        fetchPolicy: 'network-only',
        variables: { id: _id },
        onCompleted: ({ document }) => {
            form.position.setInitialValue(document.position);
            form.displayAvatar.setInitialValue(document.displayAvatar);
            form.tableauAccess.setInitialValue(document.tableauAccess);
        },
    });

    const user: Partial<User> = data?.document ?? {};

    const {
        formMessage,
        formBusy,
        onSubmit,
        onCancel,
        onDelete,
        onAlertBoxClose,
    } = useManageTemplateForm(
        ManageTemplateInstance.User,
        form,
        user,
        null,
        EDIT_USER,
        DELETE_USER,
        returnListUrl,
        _id
    );

    if (loading) return <Loader />;

    const userEmail = user?.email || '';
    const isMarexEmail =
        userEmail.endsWith('@marex.com') ||
        userEmail.endsWith('@marexspectron.com');

    const handleDelete = () => {
        if (!user._id) return;
        onDelete(user._id);
    };

    return (
        <ContainerBox>
            <Box sx={{ flex: 1, flexDirection: 'column', maxWidth: 1200 }}>
                <ManageTemplate
                    contentWidth={530}
                    formMessage={formMessage}
                    onCancel={onCancel}
                    showDelete={isAdmin && !userEmail}
                    onDelete={handleDelete}
                    onSubmit={(ev: any): void => {
                        onSubmit(ev, {
                            ...form.value,
                            version: data?.document.version,
                        });
                    }}
                    onAlertBoxClose={onAlertBoxClose}
                    isNew={false}
                    templateInstance={ManageTemplateInstance.User}
                    pathArea={'Users'}
                    returnUrl={returnListUrl}
                    formBusy={formBusy}
                    helperContent={
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                height: 80,
                                mb: 2,
                            }}
                        >
                            <LargeUserCircle
                                sx={{
                                    height: 44,
                                    width: 44,
                                    ml: 2.25,
                                    mr: 4,
                                    color: 'text.secondary',
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    mr: 4,
                                }}
                            >
                                <Typography
                                    variant="overline1"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 'fontWeightBold',
                                        mb: 0.25,
                                    }}
                                >
                                    Email
                                </Typography>
                                <Typography variant="subtitle2">
                                    {user?.email || 'Anonymous'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    mr: 4,
                                }}
                            >
                                <Typography
                                    variant="overline1"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 'fontWeightBold',
                                        mb: 0.25,
                                    }}
                                >
                                    Role
                                </Typography>
                                <Typography variant="subtitle2">
                                    {user?.role}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    mr: 4,
                                }}
                            >
                                <Typography
                                    variant="overline1"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 'fontWeightBold',
                                        mb: 0.25,
                                    }}
                                >
                                    Last signed in
                                </Typography>
                                <Typography variant="subtitle2">
                                    {moment(user?.lastLogin).format(
                                        'DD MMM YYYY'
                                    )}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    mr: 2,
                                }}
                            >
                                <Typography
                                    variant="overline1"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 'fontWeightBold',
                                        mb: 0.25,
                                    }}
                                >
                                    Member since
                                </Typography>
                                <Typography variant="subtitle2">
                                    {moment(user?.firstLogin).format(
                                        'DD MMM YYYY'
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    }
                >
                    <TextField
                        id="user-name-input"
                        sx={{ mb: 2 }}
                        disabled
                        fullWidth
                        label="Name"
                        value={user.name}
                    />
                    <TextField
                        id="user-company-input"
                        sx={{ mb: 2 }}
                        disabled
                        fullWidth
                        label="Company"
                        value={user.company}
                    />
                    <TextField
                        id="user-telephone-input"
                        sx={{ mb: 2 }}
                        disabled
                        fullWidth
                        label="Telephone number"
                        value={user.phone}
                    />
                    <FormTextField
                        id="user-function-input"
                        label="Function"
                        field={form.position}
                        placeholder="Research Analyst, Broker, Head of Sales, …"
                        charsLimit={50}
                    />
                    {isMarexEmail && (
                        <Box sx={{ '& > label': { mb: 0 } }}>
                            <Checkbox
                                id="show-contributor-photo-checkbox"
                                label="Show contributor photo"
                                value="showContributorPhoto"
                                checked={form.displayAvatar.value}
                                onChange={() =>
                                    form.displayAvatar.setValue(
                                        !form.displayAvatar.value
                                    )
                                }
                            />
                        </Box>
                    )}
                    <Box sx={{ '& > label': { mb: 0 } }}>
                        <Checkbox
                            disabled={!isAdmin}
                            label="Allow to insert tableau charts into articles"
                            value="yes"
                            checked={form.tableauAccess.value}
                            onChange={() =>
                                form.tableauAccess.setValue(
                                    !form.tableauAccess.value
                                )
                            }
                        />
                    </Box>
                </ManageTemplate>
                <UserChannelSubscriptions isAdmin={isAdmin} userId={_id} />
            </Box>
        </ContainerBox>
    );
};

export default UserEdit;
